import {openReversePatientArrivedDialog} from '@/modules/patients-therapies/components/reverse-patient-arrived-dialog/util'
import {
  formatSequenceDatetime,
  getMedicineCalculationModeLabel,
  getSequenceDrugLotNumbers,
  getSequenceMedicineNumberFormatter,
  getSequenceStandardizedMedicines,
  getSequenceTimestamp,
  isPostProduction,
  sequenceIncludesActiveSubstance,
} from '@/modules/patients-therapies/functions'
import {useSequenceValidation, useTherapySequenceActions} from '@/modules/patients-therapies/hooks'
import {usePreparationUseBy} from '@/modules/patients-therapies/hooks/use-preparation-use-by'
import {getDefaultParameterFormValues} from '@/modules/patients-therapies/schemas'
import {useExportScaleAlibisPdfReport} from '@/modules/cleanroom/api/use-export-scale-alibis-report'
import {useGetPatientTherapies} from '@/modules/patients/api/use-get-patient-therapies'
import {openPatientRecordWindow} from '@/modules/patients/components/patient-record-window/util'
import {usePrintPreparationLabel} from '@/modules/preparation-label/use-print-preparation-label'
import {usePrintPreparationReport} from '@/modules/preparation-report/use-print-preparation-report'
import {openPrintDialog} from '@/modules/production/components/dialogs/open-print-dialog'
import {useGetInactivePrescribedNames} from '@/modules/products/trade-names/api'
import {isPrescribedTradeNameLocked} from '@/modules/products/trade-names/functions'
import {
  PrescribedTradeNameCombobox,
  useAutoSelectSingleInStockPrescribedName,
  usePrescribedNameSelectItems,
} from '@/modules/products/trade-names/prescribed-name-select'
import {useGetSettings} from '@/modules/settings/api'
import {isPatientArrivalEnabled} from '@/modules/settings/functions'
import {BarcodeDialog} from '@/shared/components/ui/barcode-dialog'
import {usePatientTherapySequenceContext} from '@/shared/contexts/patient-therapy-sequence-context'
import {useCan} from '@/shared/hooks/use-permissions'
import {queries} from '@/shared/integrations/zero'
import {Alert, Badge, Box, Button, HStack, IconButton, Span, Table, Tabs, Text, VStack} from '@chakra-ui/react'
import {PatientTherapySequenceStatus} from '@cypro/engine/database/schema'
import {QaRejectReason} from '@cypro/engine/mutators'
import {PatientTherapySequence, PatientTherapySequenceMedicine} from '@cypro/engine/schema'
import {determinePreparationType, hasReachedProduction, isWeighedPreparation} from '@cypro/engine/utils'
import {getDoseChangePercent} from '@cypro/engine/utils'
import {Tooltip, dialog} from '@cypro/ui/components'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {DetailCard} from '@cypro/ui/components/ui/detail-card'
import {useStore} from '@tanstack/react-form'
import {useLocation} from '@tanstack/react-router'
import {withForm} from '@cypro/ui/integrations/form'
import {getDatetimeFormatter, joinLabels} from '@cypro/ui/utils'
import {useQuery} from '@rocicorp/zero/react'
import {TFnType, useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {LuBarcode, LuCheck, LuClipboardList, LuFileText, LuFlaskConical, LuList, LuTag, LuX} from 'react-icons/lu'
import {type NoteKind} from '../patient-therapy-sequence-drawer'
import {UseByValue} from '../../preparation-use-by/preparation-use-by'
import {PrescriptionTableHeader, PrescriptionTableRow} from '../sequence-row'
export const AboutTab = withForm({
  defaultValues: getDefaultParameterFormValues(),
  props: {} as {openNote?: NoteKind | null; onDismissNote?: () => void; openPrescribedName?: boolean},
  render: function Render({form, openNote, onDismissNote, openPrescribedName}) {
    const {t} = useTranslate('common')
    const {sequence} = usePatientTherapySequenceContext()
    const noteText =
      openNote === 'pharmacy'
        ? sequence.pharmacyNote
        : openNote === 'therapy'
          ? sequence.therapyNote || sequence.note
          : null

    return (
      <Tabs.Content value="about" spaceY="5" display="flex" flexDirection="column">
        {openNote && (
          <NoteAlert
            title={openNote === 'pharmacy' ? t('pharmacy_notes') : t('prescription_notes')}
            text={noteText}
            onClose={onDismissNote}
          />
        )}
        {sequence.status && BANNER_STATUSES.has(sequence.status) && <StatusBanner />}
        <MedicationSection form={form} openPrescribedName={openPrescribedName} />
        <PreviousPrescriptionsSection />
        <ValidationInformationSection />
      </Tabs.Content>
    )
  },
})

// final states that surface a reason banner at the top of the about tab
const BANNER_STATUSES = new Set<string>([
  PatientTherapySequenceStatus.enum.returned,
  PatientTherapySequenceStatus.enum.cancelled,
  PatientTherapySequenceStatus.enum.discarded,
])

function StatusBanner() {
  const {t} = useTranslate('common')
  const {t: tActivity} = useTranslate('activity')
  const {sequence, activities, returnedBy, cancelledBy, discardedBy} = usePatientTherapySequenceContext()
  const [returnItem] = useQuery(queries.stocks.getReturnBySequenceId(sequence.id))

  const isCancelled = sequence.status === PatientTherapySequenceStatus.enum.cancelled
  const time = sequence.returnedAt ?? sequence.cancelledAt ?? sequence.discardedAt
  const actor = returnedBy?.name ?? cancelledBy?.name ?? discardedBy?.name

  function getTitle() {
    if (sequence.status === PatientTherapySequenceStatus.enum.returned) return t('not_administered')
    if (isCancelled) return tActivity('cancelled.title')
    const aborted = (activities ?? []).some(activity => activity.event === 'PRODUCTION_ABORTED')
    return aborted ? t('aborted_in_production') : t('rejected_at_qa')
  }

  function getReason() {
    if (sequence.status === PatientTherapySequenceStatus.enum.returned) {
      const parts = [returnItem?.reason ? t(`return_reason.${returnItem.reason}`) : null, returnItem?.note]

      return parts.length ? joinLabels(parts) : null
    }

    if (isCancelled)
      return sequence.cancellationReason ? t(`abort_reason.${sequence.cancellationReason}`) : t('stop_all_treatment')

    if (!sequence.discardReason) return null
    // a QA reject has its own reasons (CY-340); a production abort and older rejects keep the abort reasons
    const isQaReject = QaRejectReason.safeParse(sequence.discardReason).success
    return joinLabels([
      t(`${isQaReject ? 'qa_reject_reason' : 'abort_reason'}.${sequence.discardReason}`),
      sequence.discardNote,
    ])
  }

  const reason = getReason()

  return (
    <Alert.Root size="sm" colorPalette={isCancelled ? 'gray' : 'red'} borderRadius="l2">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title fontSize="xs">
          {`${getTitle()}: ${joinLabels([time ? getDatetimeFormatter().format(time) : null, actor])}`}
        </Alert.Title>
        {reason && <Alert.Description fontSize="xs">{`${t('reason')}: ${reason}`}</Alert.Description>}
      </Alert.Content>
    </Alert.Root>
  )
}

const MedicationSection = withForm({
  defaultValues: getDefaultParameterFormValues(),
  props: {} as {isSequenceEditable?: boolean; openPrescribedName?: boolean},
  render: function Render({form, openPrescribedName}) {
    const {t} = useTranslate('common')
    const {sequence} = usePatientTherapySequenceContext()

    const {actions} = useTherapySequenceActions(sequence)
    const isSequenceEditable = actions.includes('PHARMACIST_VALIDATE')

    const status = sequence.status
    // production number + use-by only appear once the unit is produced
    const showProductionFields = isPostProduction(sequence)
    const showMedicationActions =
      status === PatientTherapySequenceStatus.enum.in_production ||
      status === PatientTherapySequenceStatus.enum.qa_dispatch ||
      showProductionFields

    const medicines = getSequenceStandardizedMedicines(sequence)
    const isNote = sequence.isNote
    // const hasPrescribedName = medicines.some(m => m.prescribedName?.name)

    // const concentrationDisplay = getPreparationConcentrationDisplay(
    //   {
    //     medicines:
    //       medicines.map(medicine => ({
    //         totalAmount: medicine.totalAmount,
    //         doseUnitUcum: medicine.unit?.ucumCode,
    //         drug: getTradeNameDrugData(medicine),
    //       })) ?? [],
    //     carrierVolumeMl: sequence.volume,
    //     volumeMode: sequence.volumeMode,
    //   },
    //   t
    // )

    async function openBarcode() {
      await dialog.open('bar-code-sequence', {
        children: <BarcodeDialog barcode={`S:${sequence.id}\n`} />,
        size: 'full',
      })
    }

    // function openPickingList() {
    //   openPickingListDrawer(sequence.id, {selectedOrders: [sequence]})
    // }

    return (
      <DataSection
        size="sm"
        label={
          <Box display="flex" justifyContent={'space-between'} alignItems="center" width="100%">
            <Box>{sequence.isNote ? t('guideline_note') : t('medication')}</Box>

            {!sequence.isNote && (
              <Button
                display={{base: 'none', md: 'inline-flex'}}
                size="xs"
                variant="plain"
                colorPalette="cyan"
                paddingInline="0"
                onClick={() => openBarcode()}
              >
                <LuBarcode />
                {t('see_barcode')}
              </Button>
            )}
          </Box>
        }
        labelAs="div"
        spaceY="1"
      >
        <DetailCard.Inner
          display="grid"
          gridTemplateColumns={{base: '1fr', md: '1fr 1fr'}}
          gapX="4"
          gapY="3"
          fontSize="xs"
        >
          {!isNote && (
            <>
              <DetailCard.InnerSection
                title={t('substance_dose')}
                description={
                  medicines.map(medicine => {
                    const dose = `${getSequenceMedicineNumberFormatter(medicine).format(medicine.totalAmount ?? 0)} ${medicine.unit?.ucumCode ?? ''}`
                    const calculationMode = getMedicineCalculationModeLabel(t, medicine)
                    const calculationNote = getMedicineCalculationNote(t, medicine)

                    const labels = [joinLabels([medicine.name, dose]), calculationMode]
                    if (medicines.length > 1) {
                      if (calculationNote) labels.push(calculationNote)
                    }

                    return (
                      <Span key={medicine.id} display="block">
                        <Span display="block">{joinLabels(labels)}</Span>
                        {medicines.length === 1 && calculationNote && <Span display="block">({calculationNote})</Span>}
                      </Span>
                    )
                  }) || '-'
                }
                descriptionProps={{fontWeight: 'medium'}}
                variant="muted-fg"
              />

              <DetailCard.InnerSection
                title={t('carrier_solution')}
                description={
                  sequence.carrierSolution
                    ? joinLabels([
                        sequence.carrierSolution.name,
                        sequence.volume != null ? `${sequence.volume} ml` : null,
                        sequence.volume != null ? sequence.volumeMode : null,
                      ])
                    : '-'
                }
                descriptionProps={{fontWeight: 'medium'}}
                variant="muted-fg"
              />

              <DetailCard.InnerSection
                title={t('prescription_tradename')}
                description={
                  isSequenceEditable ? (
                    <VStack gapY="2" alignItems="flex-start">
                      {sequence.medicine?.map((medicine, i) => (
                        <TradeNameSelect
                          key={medicine.id}
                          form={form}
                          medicine={medicine}
                          index={i}
                          openPrescribedName={openPrescribedName}
                        />
                      ))}
                    </VStack>
                  ) : (
                    (medicines.find(m => m.prescribedName?.name)?.prescribedName?.name ?? (
                      // no trade name has been set yet — the pharmacy picks one during validation
                      <Span color="fg.muted">{t('chosen_by_pharmacy_at_validation')}</Span>
                    ))
                  )
                }
                descriptionProps={{fontWeight: 'medium'}}
                variant="muted-fg"
              />

              <DetailCard.InnerSection
                title={t('batch_active_substance')}
                description={joinLabels(getSequenceDrugLotNumbers(sequence)) || '-'}
                descriptionProps={{fontWeight: 'medium'}}
                variant="muted-fg"
              />

              <DetailCard.InnerSection
                title={t('administration')}
                description={joinLabels([
                  sequence.applicationForm?.name,
                  sequence.duration ? t('x_min', {value: sequence.duration}) : null,
                ])}
                descriptionProps={{fontWeight: 'medium'}}
                variant="muted-fg"
              />

              <DetailCard.InnerSection
                title={t('application_date_time')}
                description={formatSequenceDatetime(sequence)}
                descriptionProps={{fontWeight: 'medium'}}
                variant="muted-fg"
              />

              {showProductionFields && (
                <>
                  {/* the production number is the prescription number, as on the label (CY-216) */}
                  <DetailCard.InnerSection
                    title={t('production_no')}
                    description={sequence.internalId ?? '-'}
                    descriptionProps={{fontWeight: 'medium'}}
                    variant="muted-fg"
                  />
                  <DetailCard.InnerSection
                    title={t('picking_list')}
                    description={sequence.order?.internalId ?? '-'}
                    descriptionProps={{fontWeight: 'medium'}}
                    variant="muted-fg"
                  />
                  <UseBySection />
                </>
              )}

              {showMedicationActions && (
                <Box gridColumnStart="1" gridColumnEnd="3">
                  <MedicationActions sequence={sequence} />
                </Box>
              )}

              {/* {hasPrescribedName && (
                <Box gridColumnStart="1" gridColumnEnd="2">
                  <Button size="xs" variant="outline" onClick={() => openPickingList()}>
                    <LuClipboardList />
                    {t('picking_list')}
                  </Button>
                </Box>
              )} */}
            </>
          )}

          {sequence.note && (
            <DetailCard.InnerSection
              title={t('guideline_note')}
              description={sequence.note}
              descriptionProps={{fontWeight: 'medium'}}
              gridColumnStart="1"
              gridColumnEnd="3"
              variant="muted-fg"
            />
          )}
        </DetailCard.Inner>
      </DataSection>
    )
  },
})

function MedicationActions({sequence}: {sequence: PatientTherapySequence}) {
  const {t} = useTranslate('common')
  const canDownloadAlibiReport = useCan('production.access')
  const showReports = hasReachedProduction(sequence)
  const {settings} = useGetSettings()
  const preparationType = determinePreparationType(sequence, settings)
  const alibiReport = useExportScaleAlibisPdfReport()
  // the same drawer opens from the Production QA tab, where a label print counts as a QA print
  const fromProduction = useLocation({select: location => location.pathname.startsWith('/production')})
  const label = usePrintPreparationLabel(sequence.id, null, fromProduction ? 'qa' : 'prescription')
  const report = usePrintPreparationReport(sequence.id)

  return (
    <HStack gapX="2" flexWrap="wrap">
      {/* the picking list is locked once production starts, so show the PDF instead of the editable drawer */}
      <Button size="xs" variant="outline" onClick={() => openPrintDialog(sequence.id)}>
        <LuClipboardList />
        {t('picking_list')}
      </Button>

      {showReports && (
        <>
          <Tooltip content={report.disabledReason}>
            <Button
              size="xs"
              variant="outline"
              onClick={report.print}
              loading={report.isPrinting}
              disabled={!report.canPrint}
            >
              <LuFileText />
              {t('production_report')}
            </Button>
          </Tooltip>
          {showReports && canDownloadAlibiReport && isWeighedPreparation(preparationType) && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => alibiReport.mutateAsync(sequence.id)}
              loading={alibiReport.isPending}
            >
              <LuFlaskConical />
              {t('alibi_report')}
            </Button>
          )}
          <Tooltip content={label.disabledReason}>
            <Button
              size="xs"
              variant="outline"
              onClick={label.print}
              loading={label.isPrinting}
              disabled={!label.canPrint}
            >
              <LuTag />
              {t('view_label')}
            </Button>
          </Tooltip>
        </>
      )}
    </HStack>
  )
}

function UseBySection() {
  const {t} = useTranslate('common')
  const {sequence} = usePatientTherapySequenceContext()
  // the same use-by the server checks before release and administration (CY-327)
  const {useBy} = usePreparationUseBy(sequence.id)

  return (
    <DetailCard.InnerSection
      title={t('use_by')}
      description={<UseByValue useBy={useBy} />}
      descriptionProps={{fontWeight: 'medium'}}
      variant="muted-fg"
    />
  )
}

function PreviousPrescriptionsSection() {
  const {t} = useTranslate('common')
  const {sequence} = usePatientTherapySequenceContext()
  const [therapies] = useGetPatientTherapies(sequence.patientId)

  const previousTherapies = useMemo(() => getPreviousTherapies(therapies || [], sequence), [therapies, sequence])

  return (
    <DataSection label={t('previous_prescriptions')} size="sm" spaceY="1">
      <DetailCard.Inner width="100%" fontSize="xs" p="0">
        <Table.Root size="sm" textStyle="xs" interactive={false}>
          <PrescriptionTableHeader />

          {previousTherapies.length ? (
            <Table.Body>
              {previousTherapies.map(therapy => (
                <PrescriptionTableRow key={therapy.id} sequence={therapy} />
              ))}
            </Table.Body>
          ) : (
            <Table.Body>
              <Table.Row>
                <Table.Cell colSpan={4}>
                  <Text color="fg.muted" paddingX="3" paddingY="2">
                    {t('no_previous_prescriptions')}
                  </Text>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          )}
        </Table.Root>

        {sequence.patient && (
          <Box pl="2" pb="2">
            <Button
              marginTop="3"
              size="xs"
              variant="outline"
              onClick={() => openPatientRecordWindow(sequence.patientId)}
            >
              <LuList />
              {t('view_full_history')}
            </Button>
          </Box>
        )}
      </DetailCard.Inner>
    </DataSection>
  )
}

function ValidationInformationSection() {
  const {t} = useTranslate('common')
  const {sequence, administeredBy, discardedBy} = usePatientTherapySequenceContext()
  const {settings} = useGetSettings()
  const {canRemoveValidation, removeValidation, canReversePatientArrival, doctorValidator, pharmacistValidator} =
    useSequenceValidation(sequence)

  const patientArrivalEnabled = isPatientArrivalEnabled(settings)

  return (
    <DataSection label={t('validation_information')} size="sm" spaceY="1">
      <DetailCard.Inner
        fontSize="xs"
        display="grid"
        gridTemplateColumns={{base: '1fr', md: '1fr 1fr'}}
        gapX="6"
        gapY="4"
      >
        <DetailCard.InnerSection
          title={t('doctor_validation')}
          description={
            <ValidationValue
              validated={Boolean(sequence.doctorValidatedAt)}
              name={doctorValidator?.name}
              time={sequence.doctorValidatedAt}
              canRemove={canRemoveValidation('doctor')}
              onRemove={() => void removeValidation('doctor')}
            />
          }
          variant="muted-fg"
        />

        <DetailCard.InnerSection
          title={t('pharmacist_validation')}
          description={
            <ValidationValue
              validated={Boolean(sequence.pharmacistValidatedAt)}
              // the pharmacist step only becomes pending once the doctor has signed off
              notStarted={!sequence.doctorValidatedAt}
              name={pharmacistValidator?.name}
              time={sequence.pharmacistValidatedAt}
              canRemove={canRemoveValidation('pharmacist')}
              onRemove={() => void removeValidation('pharmacist')}
            />
          }
          variant="muted-fg"
        />

        {patientArrivalEnabled && sequenceIncludesActiveSubstance(sequence) && (
          <DetailCard.InnerSection
            title={t('patient_arrived')}
            description={
              <ValidationValue
                validated={Boolean(sequence.patientArrivedAt)}
                time={sequence.patientArrivedAt}
                canReverse={canReversePatientArrival}
                onReverse={() => openReversePatientArrivedDialog(sequence)}
              />
            }
            variant="muted-fg"
          />
        )}

        {sequence.status === PatientTherapySequenceStatus.enum.administered && sequence.administeredAt && (
          <DetailCard.InnerSection
            title={t('administered')}
            description={joinLabels([
              getDatetimeFormatter().format(sequence.administeredAt),
              administeredBy?.name,
              // the releasing pharmacist, not a nurse at the bedside (CY-332)
              sequence.administeredOnDispatch ? t('on_dispatch', 'on dispatch') : null,
            ])}
            variant="muted-fg"
          />
        )}

        {sequence.status === PatientTherapySequenceStatus.enum.discarded && (
          <DetailCard.InnerSection
            title={t('qa_check')}
            description={
              <Span color="red.fg" fontWeight="medium" display="flex" alignItems="center" spaceX="1">
                <LuX size="1rem" />
                {joinLabels([
                  t('rejected'),
                  discardedBy?.name,
                  sequence.discardedAt ? getDatetimeFormatter().format(sequence.discardedAt) : null,
                ])}
              </Span>
            }
            variant="muted-fg"
          />
        )}
      </DetailCard.Inner>
    </DataSection>
  )
}

interface ValidationValueProps {
  validated: boolean
  /** no doctor sign-off yet — the step has not started rather than being pending */
  notStarted?: boolean
  name?: string | null
  time?: number | null
  canRemove?: boolean
  onRemove?: () => void
  canReverse?: boolean
  onReverse?: () => void
}

function ValidationValue({
  validated,
  notStarted,
  name,
  time,
  canRemove,
  onRemove,
  canReverse,
  onReverse,
}: ValidationValueProps) {
  const {t} = useTranslate('common')

  if (!validated) {
    if (notStarted) return <Span color="fg.muted">{t('not_started')}</Span>

    return (
      <Badge size="sm" variant="surface" colorPalette="orange">
        {t('pending')}
      </Badge>
    )
  }

  return (
    <Span display="flex" alignItems="center" flexWrap="wrap" gapX="2">
      <Span color="green" fontWeight="medium" display="flex" alignItems="center" spaceX="1">
        <LuCheck size="1rem" />
        {joinLabels([name, time ? getDatetimeFormatter().format(time) : null])}
      </Span>

      {canRemove && (
        <Button variant="plain" colorPalette="cyan" size="xs" paddingInline="0" onClick={onRemove}>
          {t('remove')}
        </Button>
      )}

      {canReverse && (
        <Button variant="plain" colorPalette="cyan" size="xs" paddingInline="0" onClick={onReverse}>
          {t('reverse')}
        </Button>
      )}
    </Span>
  )
}

const TradeNameSelect = withForm({
  defaultValues: getDefaultParameterFormValues(),
  props: {} as {medicine?: PatientTherapySequenceMedicine; index: number; openPrescribedName?: boolean},
  render: function Render({form, medicine, index, openPrescribedName}) {
    const {t} = useTranslate('common')
    const {items, inStockItems, noItemsInStock} = usePrescribedNameSelectItems(medicine?.activeSubstanceId)
    const isLocked = isPrescribedTradeNameLocked(medicine)
    const prescribedNameId = useStore(form.store, state => state.values.medicine?.[index]?.prescribedNameId)
    const isMissing = !prescribedNameId
    // a product saved before its trade names were made inactive is no longer offered; say why it must change (CY-259)
    const [inactiveName] = useGetInactivePrescribedNames([prescribedNameId])

    useAutoSelectSingleInStockPrescribedName(form, index, inStockItems ?? [], !isLocked)

    return (
      <form.AppField name={`medicine[${index}].prescribedNameId`} mode="value">
        {() => (
          <PrescribedTradeNameCombobox
            size="xs"
            label={t('selected_trade_name.label')}
            hideLabel
            items={items}
            required
            disabled={isLocked || noItemsInStock}
            errorMessage={
              isMissing
                ? t('required_for_validation')
                : inactiveName
                  ? t('trade_name_inactive.message', '{name} is inactive in the catalogue. Choose an active trade name.', {
                      name: inactiveName,
                    })
                  : undefined
            }
            autoOpen={Boolean(openPrescribedName && isMissing)}
            helpText={isLocked ? t('alert.aut_idem_restriction.message') : undefined}
            placeholder={
              noItemsInStock ? t('no_products_in_stock') : t('therapy_sequence.active_substance.placeholder')
            }
            portalled={false}
          />
        )}
      </form.AppField>
    )
  },
})

// selected note banner - styled like the protocol-note callout in the protocol tab
function NoteAlert({title, text, onClose}: {title: string; text: string | null; onClose?: () => void}) {
  return (
    <Alert.Root size="sm" colorPalette="orange" borderRadius="l2" display="flex" alignItems="flex-start">
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title fontSize="xs">{title}</Alert.Title>
        <Alert.Description fontSize="xs" whiteSpace="pre-wrap">
          {text}
        </Alert.Description>
      </Alert.Content>
      <IconButton
        size="xs"
        variant="ghost"
        colorPalette="orange"
        marginLeft="auto"
        alignSelf="flex-start"
        onClick={onClose}
        aria-label="close"
      >
        <LuX />
      </IconButton>
    </Alert.Root>
  )
}

// the up-to-3 prescriptions immediately before the given one chronologically,
// most recent first — future prescriptions are never included
function getPreviousTherapies(therapies: PatientTherapySequence[], current: PatientTherapySequence) {
  const currentTimestamp = getSequenceTimestamp(current)

  return therapies
    .filter(therapy => !therapy.isNote && getSequenceTimestamp(therapy) < currentTimestamp)
    .toSorted((a, b) => getSequenceTimestamp(b) - getSequenceTimestamp(a))
    .slice(0, 3)
}

// parenthetical under the dose line: the calculated (pre-reduction) dose and the reduction
// percentage, shown when the pharmacy calculation reduced the prescribed amount
function getMedicineCalculationNote(t: TFnType, medicine: PatientTherapySequenceMedicine) {
  const changePercent = getDoseChangePercent(medicine.doseProportion)
  if (changePercent === null || changePercent <= 0) return null

  const totalAmount = Number(medicine.totalAmount || 0)
  const doseFormatter = getSequenceMedicineNumberFormatter(medicine)
  const unit = medicine.unit?.ucumCode ?? ''
  const calculatedDose = totalAmount / (Number(medicine.doseProportion) / 100)

  return t('calculated_dose_reduced', {
    dose: doseFormatter.format(calculatedDose),
    unit,
    percentage: doseFormatter.format(changePercent),
  })
}
